import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export function useTrialClients() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const addTrialClientMutation = useMutation({
    mutationFn: async ({ 
      trialClientName, 
      timeSlotId, 
      bookingDate, 
      workoutTypeId 
    }: { 
      trialClientName: string; 
      timeSlotId: string; 
      bookingDate: Date; 
      workoutTypeId: string; 
    }) => {
      // Format date to YYYY-MM-DD in local timezone to avoid timezone issues
      const year = bookingDate.getFullYear();
      const month = String(bookingDate.getMonth() + 1).padStart(2, '0');
      const day = String(bookingDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      const { data, error } = await supabase.rpc('add_trial_client_to_time_slot', {
        p_trial_client_name: trialClientName,
        p_time_slot_id: timeSlotId,
        p_booking_date: formattedDate,
        p_workout_type_id: workoutTypeId
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['effective-bookings'] });
      toast(t("calendar.trialClientAddedSuccessfully"));
    },
    onError: (error) => {
      console.error("Error adding trial client:", error);
      toast.error(t("calendar.failedToAddTrialClient"));
    },
  });

  const removeTrialClientMutation = useMutation({
    mutationFn: async (trialClientId: string) => {
      const { data, error } = await supabase.rpc('remove_trial_client', {
        p_trial_client_id: trialClientId
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['effective-bookings'] });
      toast(t("calendar.trialClientRemovedSuccessfully"));
    },
    onError: (error) => {
      console.error("Error removing trial client:", error);
      toast.error(t("calendar.failedToRemoveTrialClient"));
    },
  });

  return {
    addTrialClient: addTrialClientMutation.mutate,
    removeTrialClient: removeTrialClientMutation.mutate,
    isAddingTrialClient: addTrialClientMutation.isPending,
    isRemovingTrialClient: removeTrialClientMutation.isPending,
  };
}